// src/components/team-detail/TeamDetailNextMatchCard.js
import React from "react";

// Defined outside render to avoid re-creation on each render
const BadgeOrInitial = ({ logo, name, size = "w-14 h-14", bgColor }) => {
  if (logo) {
    return <img alt={name} className={`${size} object-contain`} src={logo} />;
  }

  return (
    <div
      className={`${size} rounded-full flex items-center justify-center text-white font-bold text-sm`}
      style={{ background: bgColor || "rgba(255,255,255,0.1)" }}
    >
      {(name || "?")[0]}
    </div>
  );
};

const TeamDetailNextMatchCard = ({
  nextMatch, // { home, away, homeLogo, awayLogo, date, venue, raw }
  liveMatch, // { home, away, homeLogo, awayLogo, homeScore, awayScore, status, elapsed, raw }
  formResults, // array of last 5 results from eventslast.php
  sportsDbTeamId, // idTeam from TheSportsDB for exact matching
  decodedTeamName,
  teamColors,
  onMatchClick,
  loading, // true while Phase 1 eventsnext is pending
}) => {
  // If there's a live match, show that instead of the next match
  const displayMatch = liveMatch || nextMatch;
  const isLive = !!liveMatch;
  // Derive form dots from recent results
  // Prefer ID-based matching (exact), fallback to name matching
  const formDots = (formResults || []).slice(0, 5).map((evt) => {
    const isHome = sportsDbTeamId
      ? evt.idHomeTeam === sportsDbTeamId
      : evt.strHomeTeam
          ?.toLowerCase()
          .includes(decodedTeamName.toLowerCase()) ||
        decodedTeamName
          .toLowerCase()
          .includes(evt.strHomeTeam?.toLowerCase() || "");
    const homeScore = Number(evt.intHomeScore);
    const awayScore = Number(evt.intAwayScore);
    const teamScore = isHome ? homeScore : awayScore;
    const oppScore = isHome ? awayScore : homeScore;

    if (teamScore > oppScore) return "W";
    if (teamScore < oppScore) return "L";

    return "D";
  });

  const formColorMap = {
    W: "bg-green-500",
    D: "bg-yellow-500",
    L: "bg-red-500",
  };

  if (loading) {
    return (
      <div className="bg-white/[0.06] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-8 w-96 animate-pulse">
        <div className="h-3 w-20 bg-white/10 rounded mb-4" />
        <div className="flex items-center justify-center gap-4 mb-4">
          <div className="w-10 h-10 rounded-full bg-white/10" />
          <div className="h-4 w-8 bg-white/10 rounded" />
          <div className="w-10 h-10 rounded-full bg-white/10" />
        </div>
        <div className="h-3 w-32 bg-white/10 rounded mx-auto mb-2" />
        <div className="h-3 w-24 bg-white/10 rounded mx-auto" />
      </div>
    );
  }

  if (!displayMatch) {
    return (
      <div className="bg-white/[0.06] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-8 w-96 text-center">
        <i className="fas fa-calendar-alt text-white/15 text-2xl mb-2" />
        <p className="text-white/30 text-sm">No upcoming matches</p>
      </div>
    );
  }

  const isHomeTeam =
    displayMatch.home?.toLowerCase().includes(decodedTeamName.toLowerCase()) ||
    decodedTeamName
      .toLowerCase()
      .includes(displayMatch.home?.toLowerCase() || "");
  const opponent = isHomeTeam ? displayMatch.away : displayMatch.home;
  const opponentLogo = isHomeTeam
    ? displayMatch.awayLogo
    : displayMatch.homeLogo;
  const ourLogo = isHomeTeam ? displayMatch.homeLogo : displayMatch.awayLogo;

  const formatDate = (dateStr) => {
    if (!dateStr) return "TBD";
    try {
      return new Date(dateStr).toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "TBD";
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    try {
      return new Date(dateStr).toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  return (
    <button
      className={`backdrop-blur-xl rounded-2xl p-8 w-96 text-left hover:bg-white/[0.08] transition-all focus:outline-none focus:ring-2 cursor-pointer ${
        isLive
          ? "bg-white/[0.08] border-2 border-red-500/40 focus:ring-red-400/30"
          : "bg-white/[0.06] border border-white/[0.08] focus:ring-white/20"
      }`}
      tabIndex={0}
      onClick={() => onMatchClick?.(displayMatch.raw)}
    >
      {/* Header */}
      <div className="flex items-center justify-center gap-2 mb-5">
        {isLive && (
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
        )}
        <p
          className={`text-xs font-semibold uppercase tracking-widest text-center ${
            isLive ? "text-red-400" : "text-white/50"
          }`}
        >
          {isLive ? "Live Now" : "Next Match"}
        </p>
      </div>

      {/* Badges + score or VS */}
      <div className="flex items-center justify-center gap-6 mb-5">
        <div className="flex flex-col items-center gap-1.5">
          <BadgeOrInitial
            bgColor={`${teamColors.primary}60`}
            logo={ourLogo}
            name={isHomeTeam ? displayMatch.home : displayMatch.away}
            size="w-24 h-24"
          />
          <span className="text-[10px] text-white/30">
            {isHomeTeam ? "H" : "A"}
          </span>
        </div>
        {isLive ? (
          <div className="flex flex-col items-center">
            <span className="text-white text-2xl font-bold">
              {isHomeTeam
                ? `${liveMatch.homeScore ?? 0} - ${liveMatch.awayScore ?? 0}`
                : `${liveMatch.awayScore ?? 0} - ${liveMatch.homeScore ?? 0}`}
            </span>
            {liveMatch.elapsed && (
              <span className="text-red-400/70 text-xs mt-1">
                {liveMatch.elapsed}&apos;
              </span>
            )}
          </div>
        ) : (
          <span className="text-white/25 text-sm font-bold">VS</span>
        )}
        <div className="flex flex-col items-center gap-1.5">
          <BadgeOrInitial
            bgColor={`${teamColors.primary}60`}
            logo={opponentLogo}
            name={opponent}
            size="w-24 h-24"
          />
        </div>
      </div>

      {/* Date + Time (or live status) */}
      <p className="text-white/40 text-sm text-center mt-1.5">
        {isLive
          ? displayMatch.venue || "In Progress"
          : `${formatDate(displayMatch.date)}${formatTime(displayMatch.date) ? ` \u00B7 ${formatTime(displayMatch.date)}` : ""}`}
      </p>

      {/* Venue — only for non-live (live already shows venue above) */}
      {!isLive && displayMatch.venue && (
        <p className="text-white/25 text-xs text-center mt-1 truncate">
          {displayMatch.venue}
        </p>
      )}

      {/* Form indicator */}
      {formDots.length > 0 && (
        <div className="flex items-center justify-center gap-1.5 mt-4 pt-3 border-t border-white/[0.06]">
          <span className="text-[10px] text-white/25 mr-1">Form</span>
          {formDots.map((result, i) => (
            <div
              className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white ${formColorMap[result]}`}
              key={i}
            >
              {result}
            </div>
          ))}
        </div>
      )}
    </button>
  );
};

export default TeamDetailNextMatchCard;
